import React, { useState, useEffect, useMemo } from "react";
import { useRequest, useTranslation, useAuth } from "hooks";
import {
  Button,
  Dialog,
  Typography,
  Form,
  Select,
  Option,
  Input,
  TextArea,
  Icon,
  FileInput,
} from "components";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { endpoints } from "configs";
import { Screencast, Screenshot } from "./components";
import styles from "./Feedback.module.css";
import {
  feedbackOperations,
  feedbackSelectors,
  feedbackTypes,
  utils,
} from "./duck";

const Feedback: React.FC<{
  className: string;
  isScreenRecordingGlobal?: boolean;
  setIsScreenRecordingGlobal?: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ className, setIsScreenRecordingGlobal, isScreenRecordingGlobal }) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [isScreenshotOpened, setIsScreenshotOpened] = useState(false);
  const [isScreenRecording, setIsScreenRecording] = useState(false);
  const [isModalHidden, setIsModalHidden] = useState(false);
  const [fileForUploading, setFileForUploading] = useState<File>();
  const [inlineStyleForHtmlTag, setInlineStyleForHtmlTag] =
    useState<string>("");
  const [auth] = useAuth();
  const location = useLocation();
  const formikRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (!isScreenRecording) {
      setIsModalHidden(false);
      formikRef.current?.resetForm();
    }
  }, [location.pathname]);

  const [
    {
      data: reasonList = [],
      loading: reasonListLoading,
      firstLoading: reasonListFirstLoading,
    },
    fetchReasonList,
  ] = useRequest<feedbackTypes.Reason[]>({
    url: endpoints.feedbackReasonList,
  });

  const [{ data: suggestionListData = [] }, fetchSuggestionList] = useRequest<
    feedbackTypes.Suggestion[]
  >({
    url: endpoints.feedbackSuggestionList,
  });

  useEffect(() => {
    fetchReasonList().then(fetchSuggestionList);
  }, []);

  useEffect(() => {
    if (!inlineStyleForHtmlTag) {
      const html = window.document.querySelector("html");
      setInlineStyleForHtmlTag(html?.getAttribute("style") || "");
    }
  }, [opened]);

  const userEmail = useMemo(() => auth?.data?.user?.email || "", [auth]);

  const initialValues = useMemo(
    () => ({
      ...feedbackSelectors.validation.initialValues,
      email: utils.checkIsALIStaff(userEmail) ? "" : userEmail,
    }),
    [userEmail],
  );

  const handleHtmlTagStylesOnClose = (): void => {
    window.document.querySelector("html")?.removeAttribute("style");
  };

  const handleHtmlTagStylesOnReturn = (): void => {
    setTimeout(() => {
      if (inlineStyleForHtmlTag) {
        window.document
          .querySelector("html")
          ?.setAttribute("style", inlineStyleForHtmlTag);
      }
    });
  };

  const handleOpen = (): void => {
    setOpened(true);
    handleHtmlTagStylesOnReturn();
    if (isScreenRecording) {
      setIsScreenRecording(false);
      if (setIsScreenRecordingGlobal) {
        setIsScreenRecordingGlobal(false);
      }
      return;
    }
    if (isModalHidden) {
      setIsModalHidden(false);
    }
  };

  const handleClose = (): void => {
    if (!isModalHidden) {
      setOpened(false);
    }
    handleHtmlTagStylesOnClose();

    if (fileForUploading) {
      setFileForUploading(undefined);
    }
  };

  const handleUploadScreenshot = (file: File): void => {
    setFileForUploading(file);
    setIsScreenshotOpened(false);
    setIsModalHidden(false);
    handleHtmlTagStylesOnReturn();
  };

  useEffect(() => {
    if (isScreenRecording && !isScreenRecordingGlobal) {
      handleOpen();
    }
  }, [isScreenRecordingGlobal]);

  return (
    <div>
      <Button
        size="small"
        colorScheme={isScreenRecording ? "danger" : "info"}
        className={classNames(styles.opened, className)}
        prefix={
          isScreenRecording ? (
            <Icon name="stop-filled" size="small" />
          ) : undefined
        }
        onClick={handleOpen}
      >
        {isScreenRecording ? t("feedback.stop") : t("feedback.feedback")}
      </Button>
      <Form
        innerRef={(ref) => {
          formikRef.current = ref;
        }}
        validationSchema={feedbackSelectors.validation.schema}
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          feedbackOperations.handleSubmit({ t, values });
          setFileForUploading(undefined);
          setOpened(false);
          setIsModalHidden(false);
          setInlineStyleForHtmlTag("");
          formikHelpers.resetForm();
        }}
      >
        {({ values, handleSubmit }) => (
          <Dialog
            title={
              <Typography component="h2" className={styles.dialogTitle}>
                {t("feedback.dialogTitle")}
              </Typography>
            }
            opened={opened}
            onClose={() => {
              if (!isModalHidden) {
                handleClose();
              }
            }}
            overlayClassName={classNames(styles.overlay, {
              // guarantees return of focus after closing screenshot
              [styles.modalTransparent]: isScreenshotOpened,
              [styles.modalHidden]: isModalHidden,
            })}
          >
            <>
              <Typography variant="p2" className={styles.description}>
                {t("feedback.dialogDescription")}
              </Typography>
              <div className={styles.formContent}>
                <div className={styles.fieldReason}>
                  <Select
                    label={t("feedback.reasonForFeedback")}
                    name="reason"
                    inputClassName={styles.reasonSelect}
                    icon={<Icon name="down-outlined" />}
                    selectedIcon={<Icon name="check-outlined" />}
                    loading={reasonListLoading}
                    data={reasonList}
                    disabled={reasonListFirstLoading}
                    aria-required
                    renderItem={(reasonItem) => (
                      <Option key={reasonItem.id} value={reasonItem.id}>
                        {reasonItem.name}
                      </Option>
                    )}
                  />
                </div>
                <div className={styles.fieldName}>
                  <Input label={t("feedback.name")} name="name" required />
                </div>
                <div className={styles.fieldEmail}>
                  <Input
                    label={t("feedback.email")}
                    name="email"
                    required
                    type="email"
                  />
                </div>
                <div>
                  <TextArea
                    label={t("feedback.comments")}
                    name="comments"
                    required
                  />
                </div>
                {values.reason === "suggestion" &&
                  utils.checkIsALIStaff(values.email) && (
                    <div className={classNames(styles.optionalFields)}>
                      {suggestionListData.map((suggestion) => (
                        <div key={suggestion.id}>
                          <Input
                            label={suggestion.name}
                            name={suggestion.id}
                            className={styles.optionalField}
                          />
                          <Typography
                            component="p"
                            variant="c2"
                            className={styles.fieldNote}
                          >
                            {t("feedback.optional")}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  )}

                <div className={styles.controls}>
                  <FileInput
                    containerClassName={styles.fileUploader}
                    uploadHintClassName={styles.fileUploaderHint}
                    labelClassName={styles.fileUploaderLabel}
                    classNameFilesList={styles.filesList}
                    name="loadedFiles"
                    endpoint={endpoints.feedbackFileUpload}
                    fileForUploading={fileForUploading}
                    maxFileSize={25}
                    uploadHint={
                      <Typography variant="c2" className={styles.uploadNote}>
                        {t("feedback.fileSize")}
                      </Typography>
                    }
                  />
                  <div className={styles.screen}>
                    <Screencast
                      isRecording={isScreenRecording}
                      setIsScreenRecording={setIsScreenRecording}
                      onStartScreenRecordingCallback={() => {
                        setIsScreenRecording(true);
                        setIsModalHidden(true);
                        handleHtmlTagStylesOnClose();
                        if (setIsScreenRecordingGlobal) {
                          setIsScreenRecordingGlobal(true);
                        }
                      }}
                      onUploadCallback={(video) => {
                        setIsScreenRecording(false);
                        if (setIsScreenRecordingGlobal) {
                          setIsScreenRecordingGlobal(false);
                        }
                        setIsModalHidden(false);
                        setFileForUploading(video);
                        handleHtmlTagStylesOnReturn();
                      }}
                      onPreviewerCloseCallback={() => {
                        setIsScreenRecording(false);
                        if (setIsScreenRecordingGlobal) {
                          setIsScreenRecordingGlobal(false);
                        }
                        setIsModalHidden(false);
                        handleHtmlTagStylesOnReturn();
                      }}
                    />
                    <Button
                      variant="clear"
                      prefix={<Icon name="camera-outlined" />}
                      onClick={() => {
                        setIsScreenshotOpened(true);
                        setIsModalHidden(true);
                      }}
                    >
                      {t("feedback.takeScreenshot")}
                    </Button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className={styles.submitButton}
                  onClick={() => handleSubmit()}
                >
                  {t("feedback.sendFeedback")}
                </Button>
              </div>
            </>
          </Dialog>
        )}
      </Form>
      <Screenshot
        opened={isScreenshotOpened}
        onCloseCallback={() => {
          setIsScreenshotOpened(false);
          setIsModalHidden(false);
          handleHtmlTagStylesOnReturn();
        }}
        uploadCallback={handleUploadScreenshot}
      />
    </div>
  );
};

export default Feedback;
